import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { isAuthError, formatApiError } from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/utils/roles";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft, BookOpen, Upload, Download, Trash2, FileText, FileSpreadsheet,
  Presentation, FileImage, FileArchive, File as FileIcon, Loader2,
} from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.csv,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.zip,.mp4,.mov";

function formatBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let x = n;
  while (x >= 1024 && i < units.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function iconFor(mime, filename) {
  const m = (mime || "").toLowerCase();
  const ext = (filename || "").split(".").pop().toLowerCase();
  if (m.includes("presentation") || ext === "ppt" || ext === "pptx") return Presentation;
  if (m.includes("spreadsheet") || m.includes("excel") || ["xls", "xlsx", "csv"].includes(ext)) return FileSpreadsheet;
  if (m.startsWith("image/")) return FileImage;
  if (m.includes("zip") || ext === "zip") return FileArchive;
  if (m === "application/pdf" || ext === "pdf") return FileText;
  return FileIcon;
}

export default function TeachPage() {
  const { qualId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "staff" || user?.role === "superadmin";
  const canDelete = isSuperAdmin(user?.role);

  const [qualification, setQualification] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [description, setDescription] = useState("");
  const [uploadSection, setUploadSection] = useState("material"); // "material" | "handout"
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingName, setUploadingName] = useState("");
  const inputRef = useRef(null);

  useDocumentTitle(qualification ? `Teach · ${qualification.name}` : "Teach", "");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/teaching/qualifications/${qualId}/materials`);
      setQualification(data.qualification);
      setMaterials(data.materials || []);
    } catch (err) {
      if (!isAuthError(err)) {
        toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
        if (err.response?.status === 404) navigate("/teach");
      }
    } finally { setLoading(false); }
  }, [qualId, navigate]);

  useEffect(() => { load(); }, [load]);

  const uploadFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    if (!isStaff) { toast.error("Only staff/admin can upload"); return; }
    setUploading(true);
    let okCount = 0;
    for (const f of fileList) {
      setUploadingName(f.name);
      setUploadProgress(0);
      const fd = new FormData();
      fd.append("file", f);
      fd.append("section", uploadSection);
      if (description) fd.append("description", description);
      try {
        await api.post(`/teaching/qualifications/${qualId}/materials`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          // Reports upload progress client-side (browser → our backend).
          // Backend then streams the file to Emergent Object Storage so
          // large PPTs don't buffer in RSS.
          onUploadProgress: (evt) => {
            if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        });
        okCount++;
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err.response?.data?.detail) || "Upload failed"}`);
      }
    }
    setUploading(false);
    setUploadProgress(0);
    setUploadingName("");
    if (okCount) {
      toast.success(`${okCount} file${okCount === 1 ? "" : "s"} uploaded`);
      setDescription("");
      load();
    }
  };

  const onPickFiles = (e) => {
    uploadFiles(e.target.files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const onDownload = async (m) => {
    try {
      const res = await api.get(`/teaching/materials/${m.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: m.mime_type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = m.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const onDelete = async (m) => {
    if (!window.confirm(`Remove "${m.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/teaching/materials/${m.id}`);
      toast.success("File removed");
      setMaterials(prev => prev.filter(x => x.id !== m.id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const moveSection = async (m, section) => {
    try {
      await api.patch(`/teaching/materials/${m.id}`, { section });
      setMaterials(prev => prev.map(x => x.id === m.id ? { ...x, section } : x));
    } catch (err) {
      toast.error("Failed to move");
    }
  };

  if (loading) return <div className="py-20 text-center text-muted-foreground text-sm">Loading…</div>;
  if (!qualification) return null;

  return (
    <div className="space-y-5" data-testid="teach-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <Link to="/teach" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-to-teach">
          <ChevronLeft size={13} /> All qualifications
        </Link>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          <BookOpen size={13} /> {qualification.category} · {qualification.accreditation_body || "Internal"}
        </div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">
          {qualification.code && <span className="text-slate-400 mr-2">{qualification.code}</span>}
          {qualification.name}
        </h1>
        {qualification.description && (
          <p className="text-sm text-slate-600 mt-2">{qualification.description}</p>
        )}
      </div>

      {/* Upload zone (staff only) */}
      {isStaff && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`rounded-lg border-2 border-dashed p-6 transition ${dragOver ? "border-slate-900 bg-slate-50" : "border-slate-300 bg-white"}`}
          data-testid="upload-zone"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 font-medium text-slate-900">
                <Upload size={16} /> Add lesson plans, PowerPoints or handouts
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Drag &amp; drop files here, or click Browse. Up to 50 MB each. Allowed: PDF, Word, PowerPoint, Excel, images, ZIP, MP4.
              </p>
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Optional description for the next upload(s)…"
                className="mt-3 h-9 text-sm px-3 rounded-md border border-slate-200 bg-white w-full max-w-md focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                data-testid="upload-description"
              />
              <div className="mt-3 flex items-center gap-2 text-xs" data-testid="upload-section-picker">
                <span className="text-muted-foreground">Upload as:</span>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="upload-section" value="material" checked={uploadSection === "material"} onChange={() => setUploadSection("material")} className="accent-slate-900" />
                  Course Material
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="upload-section" value="handout" checked={uploadSection === "handout"} onChange={() => setUploadSection("handout")} className="accent-slate-900" />
                  Course Handout
                </label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACCEPT}
                onChange={onPickFiles}
                className="hidden"
                data-testid="file-input"
              />
              <Button
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="browse-files"
              >
                {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
                {uploading ? "Uploading…" : "Browse files"}
              </Button>
            </div>
          </div>
          {uploading && (
            <div className="mt-4" data-testid="upload-progress">
              <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1">
                <span className="truncate max-w-[70%]">{uploadingName}</span>
                <span className="font-medium">{uploadProgress}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-slate-900 transition-all duration-150" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* File list, grouped Course Material / Course Handout */}
      {[
        { key: "material", label: "Course Material" },
        { key: "handout", label: "Course Handout" },
      ].map(section => {
        const items = materials.filter(m => (m.section || "material") === section.key);
        return (
          <div key={section.key} className="rounded-lg border border-slate-200 bg-white" data-testid={`section-${section.key}`}>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">{section.label}</span>
                <Badge variant="secondary" className="text-[11px]">{items.length}</Badge>
              </div>
            </div>
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No {section.label.toLowerCase()} yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map(m => {
                  const Icon = iconFor(m.mime_type, m.filename);
                  return (
                    <li key={m.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`material-${m.id}`}>
                      <Icon size={20} className="text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{m.filename}</div>
                        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                          <span>{formatBytes(m.size)}</span>
                          <span>{new Date(m.uploaded_at).toLocaleDateString()}</span>
                          {m.uploaded_by_name && <span>by {m.uploaded_by_name}</span>}
                        </div>
                        {m.description && <div className="text-xs text-slate-500 mt-1 truncate">{m.description}</div>}
                      </div>
                      <div className="flex items-center gap-1">
                        {isStaff && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => moveSection(m, section.key === "material" ? "handout" : "material")}
                            className="h-8 text-xs text-slate-600 hover:bg-slate-100"
                            data-testid={`move-${m.id}`}
                            title={`Move to ${section.key === "material" ? "Handout" : "Material"}`}
                          >
                            ⇅ Move
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onDownload(m)}
                          className="h-8 text-xs"
                          data-testid={`download-${m.id}`}
                        >
                          <Download size={13} className="mr-1" /> Download
                        </Button>
                        {canDelete && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onDelete(m)}
                            className="h-8 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            data-testid={`delete-${m.id}`}
                          >
                            <Trash2 size={13} />
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
